import { z } from "zod";
//#region src/dp-max-orgs.ts
/**
* Upper bound on `maxOrgs`, and it is a fail-closed door rather than a product limit.
*
* `platform.data_placements.max_orgs` is a Postgres `integer` (int4). Without this bound a caller
* sending 3_000_000_000 gets a 500 from Postgres instead of a 400 from the validator, which reads
* to an operator as a platform fault rather than a bad request.
*/
const MAX_ORGS_MAX = 2147483647;
/**
* The wire field. `null` = unlimited (an explicit "this DP has no tenancy cap"); omitted on create
* = the same thing; omitted on PATCH = leave the limit untouched.
*
* ⛔ `.min(1)`, not `.min(0)`. `maxOrgs: 0` would mean "no organization may ever be placed here",
* which is a data plane nothing can use — unrepresentable is better than checked.
*/
const maxOrgsSchema = z.number().int().min(1).max(MAX_ORGS_MAX).nullable();
/**
* Would `orgCount` organizations on a data plane break its policy?
*
* ⛔ THE EXPLICIT null/undefined CHECKS ARE THE POINT, not defensive noise. `maxOrgs && count > maxOrgs`
* reads identically and is wrong: `0` is falsy, so a cap of zero would disable the guard it declares.
* `undefined` is accepted because a create DTO that omits the field arrives as `undefined`, and a
* caller should not have to normalise before asking.
*
* @param maxOrgs the DP's policy — `null`/`undefined` = unlimited
* @param orgCount the number of DISTINCT organizations that would be on the DP afterwards
*/
function exceedsMaxOrgs(maxOrgs, orgCount) {
	if (maxOrgs === null || maxOrgs === void 0) return false;
	return orgCount > maxOrgs;
}
/**
* The refusal body for an admission that would exceed the cap. ONE sentence, shared by all four
* write doors, so a caller cannot learn the same refusal in four phrasings.
*
* It names the limit and the attempted count because that is the whole content of the answer — a
* bare "cannot place here" sends an operator hunting for a permissions problem. There is no
* disclosure concern: this is the operator plane and the caller already holds a write grant.
*
* `dpId` is optional because DP-create refuses BEFORE the row exists, so there is no id to name yet.
*/
function maxOrgsExceededMessage(args) {
	return `Data plane ${args.dpId === void 0 ? args.dpName : `${args.dpName} (${args.dpId})`} has maxOrgs=${args.maxOrgs}; this would place ${args.attemptedOrgCount} organization(s), so the placement is refused.`;
}
/**
* The refusal body for LOWERING the cap below what the data plane already holds.
*
* ⛔ A separate sentence from {@link maxOrgsExceededMessage}, because it is a different act with a
* different remedy: nothing is being placed, and the operator must move tenants off rather than
* pick another data plane. Refusing rather than accepting is what keeps a live DP from sitting in
* violation of its own recorded policy.
*/
function maxOrgsBelowCurrentMessage(args) {
	return `Data plane ${args.dpName} (${args.dpId}) currently holds ${args.currentOrgCount} organization(s), so maxOrgs cannot be lowered to ${args.requestedMaxOrgs}. Move or remove ${args.currentOrgCount - args.requestedMaxOrgs} organization(s) first.`;
}
//#endregion
export { MAX_ORGS_MAX, exceedsMaxOrgs, maxOrgsBelowCurrentMessage, maxOrgsExceededMessage, maxOrgsSchema };

//# sourceMappingURL=dp-max-orgs.js.map